import type {
  CallAnalysisRubric,
  RubricOption,
  RubricType,
} from '@/hooks/useConfigAdmin'

/**
 * The Call Logs rubric filters' "Not graded" value. A label can't use it, or
 * picking that label would read as picking "Not graded". The save endpoint
 * (`evaluation_rubrics.py`) refuses it too.
 */
export const NOT_GRADED = '__not_graded__'

/** Mirrors the decision model's limits, enforced again by the save endpoint. */
export const MAX_LABELS = 255
/** Field lengths the save endpoint enforces (`evaluation_rubrics.py`). */
export const MAX_TITLE_LENGTH = 200
export const MAX_PROMPT_LENGTH = 20_000
export const MAX_OPTION_LABEL_LENGTH = 100
export const MAX_OPTION_DESCRIPTION_LENGTH = 2_000

/**
 * Case-insensitive comparison key, close to Python's `str.casefold()` that
 * the server uniqueness checks use: upper-casing first folds `ß` to `SS`, so
 * "Straße" and "STRASSE" collide here as they do there.
 */
export function foldCase(text: string): string {
  return text.trim().toUpperCase().toLowerCase()
}

/**
 * The Angelica tint that marks a rubric reading the agent's instructions,
 * shared by the rubric list and the call report so the two stay alike.
 */
export const AGENT_PROMPT_TINT =
  'border-angelica-subtle-border bg-angelica-subtle-bg'
export const AGENT_PROMPT_BADGE_CLASS = `${AGENT_PROMPT_TINT} text-angelica-low-contrast`
/** The switch that turns it on, in the same hue. */
export const AGENT_PROMPT_SWITCH_CLASS =
  'data-[state=checked]:bg-angelica-solid'

/**
 * The pill marking a Careforce rubric, shared by the rubric list and the
 * Metrics card so the two stay alike.
 */
export const CAREFORCE_MANAGED_LABEL = 'Careforce Managed'
export const CAREFORCE_ONLY_LABEL = 'Careforce only'
export const CAREFORCE_ONLY_HINT =
  'Only Careforce staff see this result. Customers in scope don’t.'
export const CAREFORCE_TINT =
  'border-primary-subtle-border bg-primary-subtle-bg'
export const CAREFORCE_MANAGED_BADGE_CLASS = `${CAREFORCE_TINT} text-primary-low-contrast`

/** The rubric sheet's switch rows: bordered cards, tinted while on. */
export const RUBRIC_SWITCH_ROW =
  'border-neutral-ui-border rounded-md border p-3 transition-colors'

/** A rubric saved with a blank title, in the list and its sheet. */
export const UNTITLED_RUBRIC = 'Untitled rubric'

/**
 * Where a rubric comes from: the org's own (`own`), a Careforce rubric in a
 * customer org (`managed`), or, in the Careforce org, its own rubrics that
 * customers may see (`shared`) or not (`internal`).
 */
export type RubricSource = 'own' | 'managed' | 'shared' | 'internal'

/** Each source's badge, in the rubric list and the rubric sheet alike. */
export const SOURCE_BADGES: Record<
  RubricSource,
  { label: string; className: string } | null
> = {
  own: null,
  managed: {
    label: CAREFORCE_MANAGED_LABEL,
    className: CAREFORCE_MANAGED_BADGE_CLASS,
  },
  shared: { label: 'Shared', className: CAREFORCE_MANAGED_BADGE_CLASS },
  internal: { label: 'Internal', className: 'text-muted-foreground' },
}

/** Options with anything typed in them: blank draft rows aren't counted. */
export function filledOptionCount(options: RubricOption[]): number {
  return options.filter((o) => o.label.trim() || o.description.trim()).length
}

/** One yes/no per label, all in one request: the API's question limit. */
export const MAX_MULTI_LABELS = 200
export const MAX_RATING_LEVELS = 10

export type RubricTypeInfo = {
  /** Shown as the tag on the rubric list and on call results. */
  label: string
  /** One line under the type picker. */
  hint: string
  /** What the prompt asks, as placeholder text. */
  promptPlaceholder: string
}

export const RUBRIC_TYPES: Record<RubricType, RubricTypeInfo> = {
  pass_fail: {
    label: 'Pass/fail',
    hint: 'A yes/no verdict, with the probability the call passes.',
    promptPlaceholder: 'The criteria a call has to meet to pass',
  },
  classification: {
    label: 'Single-label',
    hint: 'Picks the one label that fits best, with a probability for each.',
    promptPlaceholder: 'The question the labels answer',
  },
  multi_label: {
    label: 'Multi-label',
    hint: 'A separate yes/no for every label, so any number can apply.',
    promptPlaceholder: 'What the labels describe about the call',
  },
  rating: {
    label: 'Rating',
    hint: 'Scores the call on an ordered scale, with a probability for each level.',
    promptPlaceholder: 'What the scale measures',
  },
}

export const RUBRIC_TYPE_ORDER: RubricType[] = [
  'pass_fail',
  'classification',
  'multi_label',
  'rating',
]

/** A rubric saved before rubrics had types is pass/fail. */
export function rubricType(rubric: Pick<CallAnalysisRubric, 'type'>) {
  return rubric.type ?? 'pass_fail'
}

/** The tag for a rubric type; a type newer than this UI shows as its raw name. */
export function rubricTypeLabel(type: string | undefined | null): string {
  const key = type ?? 'pass_fail'
  return Object.hasOwn(RUBRIC_TYPES, key)
    ? RUBRIC_TYPES[key as RubricType].label
    : key
}

/**
 * What stops these options from being saved, or null when they fit the type.
 * Mirrors `_RubricInput` on the server, which has the final say.
 */
export function optionsProblem(
  type: RubricType,
  options: RubricOption[]
): string | null {
  if (type === 'pass_fail') {
    return null
  }
  if (type === 'rating') {
    if (options.length < 2 || options.length > MAX_RATING_LEVELS) {
      return `A rating needs 2 to ${MAX_RATING_LEVELS} levels.`
    }
    if (options.some((o) => !o.description.trim())) {
      return 'Describe every level, so the judge knows what each one means.'
    }
    return null
  }
  if (options.length < 2) {
    return 'Add at least 2 labels.'
  }
  const max = maxOptions(type)
  if (options.length > max) {
    return `Use at most ${max} labels.`
  }
  const labels = options.map((o) => o.label.trim())
  if (labels.some((label) => !label)) {
    return 'Name every label.'
  }
  if (new Set(labels.map(foldCase)).size !== labels.length) {
    return 'Labels must be unique.'
  }
  if (labels.includes(NOT_GRADED)) {
    return `“${NOT_GRADED}” is reserved. Use another label.`
  }
  return null
}

/** The most labels or levels a rubric of this type can have. */
export function maxOptions(type: Exclude<RubricType, 'pass_fail'>): number {
  return type === 'rating'
    ? MAX_RATING_LEVELS
    : type === 'multi_label'
      ? MAX_MULTI_LABELS
      : MAX_LABELS
}

/** The heading of a rating level: its 1-based number and optional name. */
export function ratingLevelName(index: number, label: string): string {
  const name = label.trim()
  return name ? `${index + 1} · ${name}` : String(index + 1)
}

/**
 * What an edit means for the results already saved on past calls, one note
 * per change that splits them from new calls' results. Results keep the
 * title, type and labels they were graded with, and Call Logs filters and the
 * Metrics page read them by those, so a rename shows old and new apart.
 */
export function pastResultNotes(
  before: CallAnalysisRubric,
  after: CallAnalysisRubric
): string[] {
  const notes: string[] = []
  const oldTitle = before.title.trim()
  if (oldTitle !== after.title.trim()) {
    notes.push(
      `Past results stay under “${oldTitle}”. Call Logs filters show only the new title, where past calls count as “Not graded”.`
    )
  }
  // The judge reads something different from then on, so past and new
  // verdicts pool under one title in Metrics and the Call Logs filters.
  const hadAgentPrompt = before.include_agent_prompt === true
  if (hadAgentPrompt !== (after.include_agent_prompt === true)) {
    notes.push(
      hadAgentPrompt
        ? 'Past results were judged with the agent’s instructions; new calls are judged on the transcript alone.'
        : 'Past results were judged without the agent’s instructions; new calls are judged with them.'
    )
  }
  const oldType = rubricType(before)
  const newType = rubricType(after)
  if (oldType !== newType) {
    notes.push(
      `Past results are ${RUBRIC_TYPES[oldType].label.toLowerCase()} results and won’t match filters for the new type.`
    )
    return notes
  }
  const oldOptions = before.options ?? []
  const newOptions = after.options ?? []
  if (newType === 'rating') {
    if (oldOptions.length !== newOptions.length) {
      notes.push(
        `Past results were scored on the old ${oldOptions.length}-level scale.`
      )
    }
    return notes
  }
  const kept = new Set(newOptions.map((o) => o.label.trim()))
  const dropped = oldOptions
    .map((o) => o.label.trim())
    .filter((label) => label && !kept.has(label))
  if (dropped.length > 0) {
    const shown = dropped.slice(0, 3).map((label) => `“${label}”`)
    const more = dropped.length > 3 ? ` and ${dropped.length - 3} more` : ''
    notes.push(
      `Past results keep ${shown.join(', ')}${more}, which filters will no longer offer.`
    )
  }
  return notes
}

/**
 * How a locked text field in a rubric sheet looks. Locked fields are
 * read-only rather than disabled, so their text can still be selected,
 * copied and focused.
 */
export const LOCKED_TEXT = 'read-only:bg-neutral-ui-bg read-only:cursor-default'
